import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useAppSelector } from '../../../stores/redux.store';

export type LoginTooManyAttemptsRecaptchaContextProps = {
  isRequired: boolean | undefined;
  captchaValue: string | null | undefined;
  setCaptchaValue: (_: string | null) => void;
};

export const LoginTooManyAttemptsRecaptchaContext =
  createContext<LoginTooManyAttemptsRecaptchaContextProps | null>(null);

type LoginTooManyAttemptsRecaptchaContextProviderProps = {
  children: ReactNode;
  /**
   * Determines which redux slice the hook selects from:
   *   'v1' -> `state.login` (this is the default if unspecified)
   *   'v2' -> `state.loginV2`
   *
   * Consumer supplies this based on whether the login page targets `POST /check-username` or `POST
   * /v2/check-username`, respectively.
   *
   * The option is there to support migration from the former to the latter.
   *
   * It may be removed when the migration is complete across all login page implemenations.
   *
   * See README-login-v2.md for details.
   */
  loginStateSource?: 'v1' | 'v2';
};

export const LoginTooManyAttemptsRecaptchaContextProvider = ({
  children,
  loginStateSource = 'v1',
}: LoginTooManyAttemptsRecaptchaContextProviderProps) => {
  const [captchaValue, setCaptchaValue] = useState<string | null | undefined>(null);

  const tooManyFailedLogins = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isCaptchaRequired : state.login?.isCaptchaRequired
  );
  const recaptchaEnabled = useAppSelector(state => state.appConfig.recaptchaEnable);

  const isCaptchaRequired = tooManyFailedLogins && recaptchaEnabled;

  const value = useMemo(
    (): LoginTooManyAttemptsRecaptchaContextProps => ({
      isRequired: isCaptchaRequired,
      captchaValue,
      setCaptchaValue,
    }),
    [captchaValue, setCaptchaValue, isCaptchaRequired]
  );

  return (
    <LoginTooManyAttemptsRecaptchaContext.Provider value={value}>
      {children}
    </LoginTooManyAttemptsRecaptchaContext.Provider>
  );
};

export const useLoginTooManyAttemptsRecaptchaContext = () => {
  const context = useContext(LoginTooManyAttemptsRecaptchaContext);

  if (!context) {
    throw new Error(
      'useLoginTooManyAttemptsRecaptchaContext must be used within a LoginTooManyAttemptsRecaptchaContextProvider'
    );
  }

  return context;
};
